import {
  registerApplication,
  start,
  addErrorHandler,
  type AppError,
} from "single-spa";
import type { ShellHandle } from "@observe-tech/mfe-shell-api";
import type { MfeErrorApi } from "../shell-handle/mfeError";

const SHELL_OWNED_PREFIXES = [
  "/login",
  "/logout",
  "/welcome",
  "/access-denied",
  "/not-found",
  "/test-mfe",
];
const SHELL_OWNED_EXACT = new Set(["/", "/login/callback"]);
const TIDE_PREFIX = "/tide";
const AKVA_PREFIX = "/akva-analytics";
const MARLIN_V2_PREFIX = "/marlin-v2";
const SUBMERGED_PREFIX = "/submerged";

export function isShellOwnedPath(pathname: string): boolean {
  if (SHELL_OWNED_EXACT.has(pathname)) return true;
  return SHELL_OWNED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

export interface MfeInfo {
  framework: string;
  version: string;
}

const mfeInfoRegistry: Record<string, MfeInfo> = {};
const mfeInfoSubs = new Set<() => void>();

export function getMfeInfo(name: string): MfeInfo | undefined {
  return mfeInfoRegistry[name];
}

export function subscribeMfeInfo(cb: () => void): () => void {
  mfeInfoSubs.add(cb);
  return () => {
    mfeInfoSubs.delete(cb);
  };
}

// Record an app's framework/version. Called by the MFE loader (from each MFE's
// self-reported `mfeInfo` export) and by the shell for its own runtime version.
// There is no hardcoded fallback table — anything not reported reads as unknown.
export function reportMfeInfo(name: string, info: MfeInfo): void {
  if (
    mfeInfoRegistry[name]?.framework === info.framework &&
    mfeInfoRegistry[name]?.version === info.version
  ) {
    return;
  }
  mfeInfoRegistry[name] = {
    framework: String(info.framework),
    version: String(info.version),
  };
  mfeInfoSubs.forEach((cb) => cb());
}

// Wrap a single-spa import loader so we can read the MFE's self-reported
// { mfeInfo: { framework, version } } export when it loads.
function loadMfe(name: string, url: string) {
  return async () => {
    const mod = (await import(/* @vite-ignore */ url)) as {
      mfeInfo?: MfeInfo;
    };
    if (mod?.mfeInfo) reportMfeInfo(name, mod.mfeInfo);
    return mod;
  };
}

function getMfeContainer(): HTMLElement {
  let el = document.getElementById("single-spa-app-container");
  if (!el) {
    el = document.createElement("div");
    el.id = "single-spa-app-container";
    document.body.appendChild(el);
  }
  return el;
}

let registered = false;
let started = false;
let errorHandlerRegistered = false;

export function attachMfeErrorHandler(mfeError: MfeErrorApi) {
  if (errorHandlerRegistered) return;
  errorHandlerRegistered = true;
  addErrorHandler((err: AppError) => {
    const appName =
      typeof err.appOrParcelName === "string"
        ? err.appOrParcelName
        : "unknown-mfe";
    console.error("[shell] MFE error", appName, err);
    mfeError.set(appName, err);
  });
}

export function registerMfes(handle: ShellHandle) {
  if (registered) return;
  registered = true;

  const customProps = () => ({
    shellHandle: handle,
    domElement: getMfeContainer(),
    domElementGetter: () => getMfeContainer(),
  });

  const tideUrl = import.meta.env.VITE_TIDE_MFE_URL || "/tide/mfe-entry.js";
  if (tideUrl) {
    registerApplication({
      name: "@observe/tide",
      app: loadMfe("@observe/tide", tideUrl),
      activeWhen: (loc) => loc.pathname.startsWith(TIDE_PREFIX),
      customProps,
    });
  }

  const akvaUrl =
    import.meta.env.VITE_AKVA_MFE_URL || "/akva-analytics/mfe-entry.js";
  if (akvaUrl) {
    registerApplication({
      name: "@observe/akva-analytics",
      app: loadMfe("@observe/akva-analytics", akvaUrl),
      activeWhen: (loc) => loc.pathname.startsWith(AKVA_PREFIX),
      customProps,
    });
  }

  const marlinV2Url =
    import.meta.env.VITE_MARLIN_V2_MFE_URL || "/marlin-v2/mfe-entry.js";
  if (marlinV2Url) {
    registerApplication({
      name: "@observe/marlin-v2",
      app: loadMfe("@observe/marlin-v2", marlinV2Url),
      activeWhen: (loc) => loc.pathname.startsWith(MARLIN_V2_PREFIX),
      customProps,
    });
  }

  const submergedUrl = import.meta.env.VITE_SUBMERGED_MFE_URL;
  if (submergedUrl) {
    registerApplication({
      name: "@observe/submerged",
      app: loadMfe("@observe/submerged", submergedUrl),
      activeWhen: (loc) => loc.pathname.startsWith(SUBMERGED_PREFIX),
      customProps,
    });
  }
}

export function startSingleSpa() {
  if (started) return;
  started = true;
  start({ urlRerouteOnly: true });
}
